import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Apple,
  Download,
  ExternalLink,
  QrCode,
  RefreshCw,
  Smartphone,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { api, apiError } from '@/lib/api-client';
import { useUiStore } from '@/stores/uiStore';
import { useAuthStore } from '@/stores/sessionStore';
import { QrCodeSvg } from '@/components/ui/QrCode';

type PlatformLinks = {
  preview: string;
  production: string;
  store: string;
};

type DownloadLinks = {
  android: PlatformLinks;
  ios: PlatformLinks;
  /** ISO-8601 timestamp of the last admin save; null when no admin
   *  has ever saved download links. */
  updatedAt: string | null;
  /** Per-slot last-changed timestamps. Keys are
   *  `<platform>.<slot>` like `android.preview` / `ios.production`.
   *  Empty / not yet stamped slots are absent. */
  timestamps: Record<string, string>;
};

type QrTokenResponse = { token: string; expiresAt: string };

type SlotKey = 'production' | 'preview' | 'store';

type Slot = {
  key: SlotKey;
  url: string;
  /** Translation key for the heading. */
  titleKey: string;
  /** Translation key for the helper text. */
  hintKey: string;
  /** Translation key for the action button label. */
  ctaKey: string;
  /** Whether the action is a download (vs. opening a store page). */
  isDownload: boolean;
};

function buildSlots(links: PlatformLinks): Slot[] {
  // Order: production first (what most field users want), then preview
  // (for testers), then the store listing.
  return [
    {
      key: 'production',
      url: links.production?.trim() ?? '',
      titleKey: 'common:download.slot.productionTitle',
      hintKey: 'common:download.slot.productionHint',
      ctaKey: 'common:download.slot.productionCta',
      isDownload: true,
    },
    {
      key: 'preview',
      url: links.preview?.trim() ?? '',
      titleKey: 'common:download.slot.previewTitle',
      hintKey: 'common:download.slot.previewHint',
      ctaKey: 'common:download.slot.previewCta',
      isDownload: true,
    },
    {
      key: 'store',
      url: links.store?.trim() ?? '',
      titleKey: 'common:download.slot.storeTitle',
      hintKey: 'common:download.slot.storeHint',
      ctaKey: 'common:download.slot.storeCta',
      isDownload: false,
    },
  ];
}

function PlatformSection({
  links,
  title,
  Icon,
  emptyKey,
  qrOpen,
  toggleQr,
  platform,
  timestamps,
}: {
  links: PlatformLinks;
  title: string;
  Icon: LucideIcon;
  emptyKey: string;
  qrOpen: Record<string, boolean>;
  toggleQr: (k: string) => void;
  /** "android" or "ios" — used to look up per-slot timestamps. */
  platform: 'android' | 'ios';
  /** Per-slot ISO timestamps from the API. */
  timestamps: Record<string, string>;
}) {
  const { t, i18n } = useTranslation(['common']);
  const slots = buildSlots(links);
  const visible = slots.filter((s) => !!s.url);

  const fmtUpdated = (iso: string | undefined) => {
    if (!iso) return null;
    return new Date(iso).toLocaleDateString(i18n.language, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Card>
      <CardHeader>
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
          <Icon className="h-4 w-4" /> {title}
        </h2>
      </CardHeader>
      <CardBody>
        {visible.length === 0 ? (
          <p className="text-sm italic text-slate-500 dark:text-slate-400">
            {t(emptyKey)}
          </p>
        ) : (
          <div className="space-y-4">
            {visible.map((s, idx) => {
              const qrKey = `${title}:${s.key}`;
              const updatedAt = fmtUpdated(timestamps[`${platform}.${s.key}`]);
              return (
                <div
                  key={s.key}
                  className={
                    idx > 0
                      ? 'border-t border-slate-100 pt-4 dark:border-slate-800'
                      : ''
                  }
                >
                  <div className="flex flex-col gap-1.5">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                        {t(s.titleKey)}
                      </h3>
                      {updatedAt ? (
                        <span className="text-[11px] text-slate-400 dark:text-slate-500">
                          {t('common:download.slotUpdated', {
                            defaultValue: 'Actualizat: {{when}}',
                            when: updatedAt,
                          })}
                        </span>
                      ) : null}
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {t(s.hintKey)}
                    </p>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        {...(s.isDownload ? { download: true } : {})}
                      >
                        <Button>
                          {s.isDownload ? (
                            <Download className="mr-1 h-4 w-4" />
                          ) : (
                            <ExternalLink className="mr-1 h-4 w-4" />
                          )}
                          {t(s.ctaKey)}
                        </Button>
                      </a>
                      <Button
                        variant="secondary"
                        onClick={() => toggleQr(qrKey)}
                      >
                        <QrCode className="mr-1 h-4 w-4" />
                        {qrOpen[qrKey]
                          ? t('common:download.hideQr')
                          : t('common:download.showQr')}
                      </Button>
                    </div>
                    {qrOpen[qrKey] ? (
                      <div className="mt-2 flex flex-col items-center gap-2 self-start rounded-lg bg-white p-3 ring-1 ring-slate-200 dark:bg-slate-100">
                        <QrCodeSvg value={s.url} size={200} />
                        <p className="max-w-[220px] break-all text-center text-[10px] text-slate-500">
                          {s.url}
                        </p>
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

export function DownloadAppPage() {
  const { t, i18n } = useTranslation(['common', 'auth']);
  const pushToast = useUiStore((s) => s.pushToast);

  const linksQ = useQuery<DownloadLinks>({
    queryKey: ['settings', 'download'],
    queryFn: () => api.get('settings/download').json<DownloadLinks>(),
  });

  // Per-slot QR-toggle state. Keyed by `${platform}:${slotKey}` so we
  // don't entangle Android-production with iOS-production.
  const [qrOpen, setQrOpen] = useState<Record<string, boolean>>({});
  const toggleQr = (k: string) =>
    setQrOpen((prev) => ({ ...prev, [k]: !prev[k] }));

  // QR autologin token (for the mobile app to sign the user in by
  // scanning a code generated here). Issued lazily AND tied to the
  // currently-signed-in user — without that tie, a cached
  // `qrTokenMut.data` from user A would still display its QR after
  // user A logs out and user B logs in, and the mobile scanner would
  // exchange it for user A's session (token is bound server-side to
  // its issuer).
  const currentUserId = useAuthStore((s) => s.user?.id ?? null);
  const [showQrLogin, setShowQrLogin] = useState(false);
  const qrTokenMut = useMutation({
    mutationFn: () => api.post('auth/qr-token').json<QrTokenResponse>(),
    onError: async (e) => {
      const err = await apiError(e);
      pushToast({ variant: 'error', message: err.message });
    },
  });

  // Drop any cached QR token whenever the signed-in user changes (and
  // hide the QR card so the next view starts clean). Belt-and-braces
  // alongside the per-open re-issue below.
  useEffect(() => {
    qrTokenMut.reset();
    setShowQrLogin(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId]);

  // Every time the user opens the QR card, force a fresh token. This
  // prevents stale-QR scenarios where the previously-issued token is
  // re-displayed across reopens (and stays valid for its 5 min TTL).
  useEffect(() => {
    if (showQrLogin && !qrTokenMut.isPending) {
      qrTokenMut.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showQrLogin]);

  const qrLoginPayload = useMemo(() => {
    const tok = qrTokenMut.data?.token;
    if (!tok) return null;
    return `forest://qr-login?t=${encodeURIComponent(tok)}`;
  }, [qrTokenMut.data]);

  const expiresIn = useMemo(() => {
    const exp = qrTokenMut.data?.expiresAt;
    if (!exp) return null;
    const ms = new Date(exp).getTime() - Date.now();
    if (ms <= 0) return 0;
    return Math.ceil(ms / 1000);
  }, [qrTokenMut.data]);

  const android = linksQ.data?.android ?? { preview: '', production: '', store: '' };
  const ios = linksQ.data?.ios ?? { preview: '', production: '', store: '' };
  const timestamps = linksQ.data?.timestamps ?? {};
  const updatedAt = linksQ.data?.updatedAt ?? null;

  // Visiting this page dismisses the "NEW" badge in the sidebar.
  // Stamps the most recent slot timestamp into localStorage so the
  // next render of the sidebar treats everything as seen.
  useEffect(() => {
    const all = Object.values(timestamps);
    if (all.length === 0) return;
    const max = all.reduce((acc, v) => (v > acc ? v : acc), '');
    if (!max) return;
    try {
      localStorage.setItem('forest.downloadLinksSeenAt', max);
    } catch {
      // Storage may be unavailable (private mode) — silent fallback.
    }
  }, [timestamps]);
  const updatedLabel = updatedAt
    ? new Date(updatedAt).toLocaleString(i18n.language, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  return (
    <div className="animate-fade-in mx-auto max-w-3xl space-y-6 py-6">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-forest-100 text-forest-700">
          <Smartphone className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
            {t('common:download.title')}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t('common:download.subtitle')}
          </p>
          {updatedLabel ? (
            <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
              {t('common:download.lastUpdated', {
                defaultValue: 'Linkuri actualizate: {{when}}',
                when: updatedLabel,
              })}
            </p>
          ) : null}
        </div>
      </div>

      <PlatformSection
        links={android}
        title={t('common:download.androidTitle')}
        Icon={Smartphone}
        emptyKey="common:download.androidNotConfigured"
        qrOpen={qrOpen}
        toggleQr={toggleQr}
        platform="android"
        timestamps={timestamps}
      />

      <PlatformSection
        links={ios}
        title={t('common:download.iosTitle')}
        Icon={Apple}
        emptyKey="common:download.iosNotAvailable"
        qrOpen={qrOpen}
        toggleQr={toggleQr}
        platform="ios"
        timestamps={timestamps}
      />

      {/* QR autologin */}
      <Card>
        <CardHeader>
          <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
            <QrCode className="h-4 w-4" /> {t('auth:download.qrLoginTitle')}
          </h2>
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            {t('auth:download.qrLoginSubtitle')}
          </p>
        </CardHeader>
        <CardBody className="space-y-3">
          {!showQrLogin ? (
            <Button onClick={() => setShowQrLogin(true)}>
              <QrCode className="mr-1 h-4 w-4" />
              {t('auth:download.qrLoginShow')}
            </Button>
          ) : qrTokenMut.isPending && !qrTokenMut.data ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {t('common:state.loading')}
            </p>
          ) : qrLoginPayload ? (
            <div className="flex flex-col items-center gap-3">
              <QrCodeSvg value={qrLoginPayload} size={240} />
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {expiresIn !== null && expiresIn > 0
                  ? t('auth:download.qrLoginExpiresIn', { seconds: expiresIn })
                  : t('auth:download.qrLoginExpired')}
              </p>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={() => qrTokenMut.mutate()}>
                  <RefreshCw className="mr-1 h-4 w-4" />
                  {t('auth:download.qrLoginRefresh')}
                </Button>
                <Button variant="ghost" onClick={() => setShowQrLogin(false)}>
                  {t('auth:download.qrLoginHide')}
                </Button>
              </div>
            </div>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}
